import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import DataTable from "../components/DataTable";
import RegisterCarModel from "../components/RegisterCarModel";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import CarTypeList from "./CarTypeList";

type DataIndex = keyof Types.CarModelDto;

function CarModelList({ categoryId }: { categoryId: string }) {
    useEffect(() => {
        requestGetCarModels(categoryId);
        setcurrentCarModel(undefined);
    }, [categoryId]);

    const alert = useAlert();

    const [carModels, setCarModels] = useState<Array<Types.CarModelDto>>();
    const [currentCarModel, setcurrentCarModel] = useState<Types.CarModelDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteCarModel, setOpenDeleteCarModel] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.CarModelDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.CarModelDto> = [
        {
            align: "center",
            title: String.carModelId,
            dataIndex: "modelId",
            key: "modelId",
            ...getColumnSearchProps("modelId"),
        },
        {
            align: "center",
            title: String.carModelName,
            dataIndex: "modelName",
            key: "modelName",
            ...getColumnSearchProps("modelName"),
        },
        {
            align: "center",
            title: String.unavailable,
            dataIndex: "unavailable",
            key: "unavailable",
            render: (_, { unavailable }) => {
                return unavailable !== null ? <span>{unavailable.toString()}</span> : "";
            },
        },
        {
            align: "center",
            title: String.carCategoryName,
            dataIndex: ["category", "categoryName"],
            key: "categoryName",
            render: (_, { category }) => {
                return <span>{category.categoryName}</span>;
            },
            ...getColumnSearchProps("category"),
        },
    ];

    const requestAxiosGetCarModels = async (categoryId: number) => {
        let response = await utilAxiosWithAuth().get(Request.CAR_MODEL_LIST_URL);
        if (categoryId !== undefined) {
            response = await utilAxiosWithAuth().get(Request.CAR_MODEL_LIST_URL + "?categoryId=" + categoryId);
        }

        // console.log(response.data);
        return response.data;
    };

    const {
        loading: loadingGetCarModels,
        error: errorGetCarModels,
        data: resultGetCarModels,
        execute: requestGetCarModels,
    } = useAsyncAxios(requestAxiosGetCarModels);

    useEffect(() => {
        if (resultGetCarModels === null) return;

        setCarModels(resultGetCarModels.carModels);
    }, [resultGetCarModels]);

    useEffect(() => {
        if (errorGetCarModels === null) return;

        alert.setAlert(errorGetCarModels.response.data.code, "Error", errorGetCarModels.response.data.message);
    }, [errorGetCarModels]);

    const requestAxiosRemoveCarModel = async (carModel: Types.CarModelDto) => {
        const response = await utilAxiosWithAuth().delete(Request.CAR_MODEL_URL + "/" + carModel?.modelId);
        return response.data;
    };

    const {
        loading: loadingRemoveCarModel,
        error: errorRemoveCarModel,
        data: resultRemoveCarModel,
        execute: requestRemoveCarModels,
    } = useAsyncAxios(requestAxiosRemoveCarModel);

    useEffect(() => {
        if (resultRemoveCarModel === null) return;

        alert.setAlert(AlertType.SUCCESS, "모델 삭제 성공", "모델 삭제를 완료하였습니다.");

        requestGetCarModels();
    }, [resultRemoveCarModel]);

    useEffect(() => {
        if (errorRemoveCarModel === null) return;

        alert.setAlert(errorRemoveCarModel.response.data.code, "Error", errorRemoveCarModel.response.data.message);
    }, [errorGetCarModels]);

    useEffect(() => {
        requestGetCarModels();
    }, []);

    const onRow = (value: Types.CarModelDto) => {
        setcurrentCarModel(value);
    };

    const handleDeleteCarModel = () => {
        if (currentCarModel !== undefined) setOpenDeleteCarModel(true);
    };

    const handleDeleteCarModelOk = () => {
        setOpenDeleteCarModel(false);
        requestRemoveCarModels(currentCarModel);
    };

    const handleDeleteCarModelCancel = () => {
        setOpenDeleteCarModel(false);
    };

    const onRegisterCarModel = () => {
        requestGetCarModels();
        setEditMode("none");
    };

    return (
        <>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <Row justify={"space-between"}>
                    <Col span={6}>
                        <Divider orientation="left">
                            <span>모델</span>
                        </Divider>
                    </Col>
                    <Space>
                        <Button
                            onClick={() => {
                                editMode === "add" ? setEditMode("none") : setEditMode("add");
                            }}
                        >
                            {String.userAdd}
                        </Button>
                        <Button
                            onClick={() => {
                                editMode === "update" ? setEditMode("none") : setEditMode("update");
                            }}
                        >
                            {String.userUpdate}
                        </Button>
                        <Button onClick={() => handleDeleteCarModel()} danger>
                            {String.userRemove}
                        </Button>
                    </Space>
                </Row>
            </Space>
            <DataTable hasScroll={false} columns={columns} rowKey="modelId" dataSource={carModels} onRow={onRow} />

            {editMode === "add" && <RegisterCarModel editMode={editMode} onRegisterCarModel={onRegisterCarModel} />}

            {editMode === "update" && currentCarModel !== undefined && (
                <RegisterCarModel editMode={editMode} carModel={currentCarModel} onRegisterCarModel={onRegisterCarModel} />
            )}

            {openDeleteCarModel && (
                <Modal open={openDeleteCarModel} onOk={handleDeleteCarModelOk} onCancel={handleDeleteCarModelCancel}>
                    {currentCarModel?.modelName} 삭제하시겠습니까?
                </Modal>
            )}

            {currentCarModel !== undefined && <CarTypeList modelId={currentCarModel.modelId.toString()} />}
        </>
    );
}

export default CarModelList;
